import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, clearAuthData, fetchUsers as utilFetchUsers, fetchSites as utilFetchSites, fetchRoles as utilFetchRoles, fetchDepartments as utilFetchDepartments } from '../../utils/api';
import { trackPageView } from '../../utils/pageTracking';
import { trackFeatureFirstUse } from '../../utils/featureTracking';
import { updateSessionDevice } from '../../utils/deviceTracking';
import { SubscriptionProvider, useSubscription } from '../../context/SubscriptionContext';
import { GracePeriodBanner, SubscriptionRestrictedOverlay } from '../subscription';
import GuidedTour from '../GuidedTour';

// Inner layout component that uses subscription context
const AdminLayoutInner = () => {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isChecking, setIsChecking] = useState(true);
  const [pendingExpensesCount, setPendingExpensesCount] = useState(0);
  const [pendingOvertimeCount, setPendingOvertimeCount] = useState(0);
  const [pendingHolidaysCount, setPendingHolidaysCount] = useState(0);
  const [pendingProcurementCount, setPendingProcurementCount] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [subscriptionFeatures, setSubscriptionFeatures] = useState(null);
  const [organisationName, setOrganisationName] = useState('');
  const [organisationLogo, setOrganisationLogo] = useState('');
  const [tooltipPos, setTooltipPos] = useState({left: 0, top: 0});
  const [isSmallScreenDevice, setIsSmallScreenDevice] = useState(false);
  const [deviceCheckComplete, setDeviceCheckComplete] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showGuidedTour, setShowGuidedTour] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Subscription status from context
  const { isRestricted, showGraceBanner } = useSubscription();
  
  // Check if current page is the subscription plans page (always allowed)
  const isSubscriptionPage = location.pathname === '/admin/plans';
  
  // Show restriction overlay only if restricted AND not on subscription page
  const showRestrictionOverlay = isRestricted && !isSubscriptionPage;

  // Routes accessible on mobile devices
  const mobileAccessibleRoutes = [
    '/admin/dashboard',
    '/admin/neo-credits',
    '/admin/plans'
  ];

  // Check if current route is accessible on mobile
  const isCurrentRouteAccessible = mobileAccessibleRoutes.some(route => 
    location.pathname === route || location.pathname.startsWith(route + '/')
  );

  // Small screen device detection - runs once on mount after auth check
  // This is intentionally difficult to bypass by checking multiple signals
  useEffect(() => {
    if (!isChecking) {
      const detectSmallScreenDevice = () => {
        // Check 1: User agent for mobile devices
        const userAgent = navigator.userAgent.toLowerCase();
        const mobileKeywords = [
          'android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 
          'windows phone', 'opera mini', 'mobile', 'tablet'
        ];
        const isMobileUserAgent = mobileKeywords.some(keyword => userAgent.includes(keyword));
        
        // Check 2: Touch capability as primary input
        const isTouchPrimary = window.matchMedia('(pointer: coarse)').matches;
        
        // Check 3: Screen dimensions (physical screen, not viewport)
        // 14" screen at 1920x1080 is roughly 157 PPI, so ~1366px width minimum
        // We'll use 1024px as minimum width threshold
        const screenWidth = window.screen.width;
        const screenHeight = window.screen.height;
        const smallerDimension = Math.min(screenWidth, screenHeight);
        const largerDimension = Math.max(screenWidth, screenHeight);
        
        // Check 4: Device pixel ratio (mobile devices often have higher ratios)
        const pixelRatio = window.devicePixelRatio || 1;
        
        // Check 5: Viewport width
        const viewportWidth = window.innerWidth;
        
        // Determine if this is a small screen device
        // A device is considered "small screen" if:
        // - It has mobile user agent OR touch is primary input
        // - AND screen width is less than 1024px (accounting for rotation)
        // - OR viewport is very small AND has high pixel ratio (typical of phones)
        
        const isLikelyMobileDevice = isMobileUserAgent || isTouchPrimary;
        const hasSmallScreen = largerDimension < 1024;
        const hasVerySmallViewport = viewportWidth < 768 && pixelRatio > 1.5;
        
        // Gate if: mobile device with small screen, OR very small viewport on high-DPI
        const shouldGate = (isLikelyMobileDevice && hasSmallScreen) || 
                          (isLikelyMobileDevice && hasVerySmallViewport);
        
        return shouldGate;
      };
      
      // Run detection
      const isSmall = detectSmallScreenDevice();
      setIsSmallScreenDevice(isSmall);
      setDeviceCheckComplete(true);
      
      // Also check on resize (in case of desktop browser resize for testing)
      // But we remember the INITIAL mobile detection to prevent bypass
      const initialIsMobile = isSmall;
      
      const handleResize = () => {
        // If initially detected as mobile, STAY gated regardless of resize
        // This prevents bypassing by rotating or resizing browser
        if (initialIsMobile) {
          setIsSmallScreenDevice(true);
        }
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isChecking]);

  // Reset scroll position when route changes (mobile focus)
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Track page view for analytics
    const pageNameMap = {
      '/admin/dashboard': 'Dashboard',
      '/admin/sites': 'Sites',
      '/admin/site-assigner': 'Site Assigner',
      '/admin/procurement': 'Materials',
      '/admin/users': 'Team',
      '/admin/tasks': 'Tasks',
      '/admin/team-capacity': 'Team Capacity',
      '/admin/reports/attendance': 'Team Performance',
      '/admin/expenses': 'Expenses',
      '/admin/overtime': 'Overtime',
      '/admin/holidays': 'Holidays',
      '/admin/payments': 'Team Payments',
      '/admin/invoices': 'CIS Statements',
      '/admin/quoting': 'Quotes & Invoices',
      '/admin/clients': 'Clients',
      '/admin/reports/work-diary': 'Work Diary',
      '/admin/reports/progress': 'Progress Reports',
      '/admin/photo-gallery': 'Photo Gallery',
      '/admin/team-dashboard-settings': 'Admin Settings',
      '/admin/plans': 'Subscription Plans',
      '/admin/neo-credits': 'Neo Credits',
      '/admin/user-preferences': 'User Settings'
    };
    
    const pageName = pageNameMap[location.pathname];
    if (pageName) {
      trackPageView(pageName);
      trackFeatureFirstUse(pageName);  // Track first-time feature usage
    }
  }, [location.pathname]);

  // Authentication check and data fetching
  useEffect(() => {
    const checkAuthAndFetchData = async () => {
      // Check authentication
      const token = localStorage.getItem('token');
      const userType = localStorage.getItem('userType');
      
      if (!token || userType !== 'admin') {
        // Keep isChecking = true (show spinner) while redirecting
        navigate('/admin/login', { replace: true });
        return;  // Early return - don't proceed
      }

      // Set up axios defaults
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsChecking(false);
      
      // Update session with device info (once per session)
      updateSessionDevice();

      // Fetch data
      await fetchData();
    };

    checkAuthAndFetchData();
  }, [navigate]);

  // Periodic refresh of pending expenses, overtime, holidays, and procurement count
  useEffect(() => {
    if (!isChecking && !isLoading) {
      // Refresh every 30 seconds to keep count updated
      const interval = setInterval(() => {
        fetchPendingExpensesCount();
        fetchPendingOvertimeCount();
        fetchPendingHolidaysCount();
        fetchPendingProcurementCount();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [isChecking, isLoading]);

  // Fetch pending expenses count
  const fetchPendingExpensesCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/expenses`, {
        params: { status: 'pending' },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPendingExpensesCount((response.data || []).length);
    } catch (error) {
      console.error('Failed to fetch pending expenses count:', error);
      setPendingExpensesCount(0);
    }
  };

  // Fetch pending overtime count
  const fetchPendingOvertimeCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/overtime`, {
        params: { status: 'pending' },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPendingOvertimeCount((response.data || []).length);
    } catch (error) {
      console.error('Failed to fetch pending overtime count:', error);
      setPendingOvertimeCount(0);
    }
  };

  // Fetch pending holidays count
  const fetchPendingHolidaysCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/holidays`, {
        params: { status: 'pending' },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPendingHolidaysCount((response.data || []).length);
    } catch (error) {
      console.error('Failed to fetch pending holidays count:', error);
      setPendingHolidaysCount(0);
    }
  };

  // Fetch pending procurement count
  const fetchPendingProcurementCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/procurement/materials`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      // Filter for pending status (materials that haven't been placed yet)
      const pendingMaterials = (response.data || []).filter(m => m.procurement_status === 'pending');
      setPendingProcurementCount(pendingMaterials.length);
    } catch (error) {
      console.error('Failed to fetch pending procurement count:', error);
      setPendingProcurementCount(0);
    }
  };


  // Fetch current user info
  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  };

  // Check if user should see the guided tour
  const checkGuidedTour = async () => {
    // Don't show guided tour on small screens where sidebar isn't visible
    if (window.innerWidth < 1024) return;
    
    try {
      const response = await axios.get(`${API}/admin/preferences`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const preferences = response.data.data || response.data;
      // Show guided tour if user hasn't completed it
      if (!preferences.has_completed_guided_tour) {
        // Small delay to ensure the sidebar is fully rendered
        setTimeout(() => setShowGuidedTour(true), 1000);
      }
    } catch (error) {
      console.error('Failed to check guided tour status:', error);
    }
  };

  // Fetch organization subscription features
  const fetchSubscriptionFeatures = async () => {
    try {
      const response = await axios.get(`${API}/admin/organisation`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSubscriptionFeatures(response.data.subscription_features || {});
      setOrganisationName(response.data.name || '');
      setOrganisationLogo(response.data.logo || '');
    } catch (error) {
      console.error('Failed to fetch subscription features:', error);
    }
  };

  // Fetch data function (accessible throughout component)
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [usersData, sitesData, rolesData, departmentsData] = await Promise.all([
        utilFetchUsers(),
        utilFetchSites(),
        utilFetchRoles(),
        utilFetchDepartments()
      ]);
      
      setUsers(usersData);
      setSites(sitesData);
      setRoles(rolesData);
      setDepartments(departmentsData);
      
      // Fetch current user and pending counts after data is loaded
      fetchCurrentUser();
      fetchSubscriptionFeatures();
      fetchPendingExpensesCount();
      fetchPendingOvertimeCount();
      fetchPendingHolidaysCount();
      fetchPendingProcurementCount();
      checkGuidedTour();
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Don't throw error, just log it and continue with empty data
    }
    setIsLoading(false);
  };

  const handleLogout = async () => {
    // Notify backend so this device's session is invalidated server-side
    // (other devices stay signed in). Best-effort — local clear still runs
    // even if the API call fails.
    try {
      const deviceId = localStorage.getItem('deviceId');
      if (deviceId) {
        await axios.post(`${API}/admin/logout`, { device_id: deviceId });
      }
    } catch (e) {
      console.warn('Backend admin logout call failed (continuing with local clear):', e);
    }
    clearAuthData();
    navigate('/admin/login');
  };

  // Safe navigation helper - checks for unsaved changes in Reports before navigating
  const safeNavigate = (path) => {
    // Check if we're on the reports page and have unsaved changes
    if (location.pathname.includes('/reports/progress') && window.__REPORTS_UNSAVED_CHANGES__) {
      // Show the navigation blocker modal in Reports component
      if (window.__REPORTS_SHOW_NAV_BLOCKER__) {
        window.__REPORTS_SHOW_NAV_BLOCKER__(path);
        return;
      }
    }
    // No unsaved changes or not on reports page, navigate normally
    navigate(path);
  };

  // SVG Icon Components for Main Menu
  const getMenuIcon = (key) => {
    const iconClass = "w-5 h-5 text-white";
    switch(key) {
      case 'dashboard':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        );
      case 'sites':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'users':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'requests':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'admin':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'settings':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      case 'team-dashboard-settings':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
        );
      case 'user-preferences':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case 'neo-credits':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'plans':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        );
      default:
        return null;
    }
  };

  // SVG Icon Components for Sub-Menu
  const getSubMenuIcon = (key, isActive, isInDevelopment = false) => {
    const iconClass = `w-4 h-4 ${isActive ? 'text-indigo-300' : isInDevelopment ? 'text-orange-400' : 'text-gray-400'}`;
    switch(key) {
      // Sites submenu
      case 'site-management':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'site-assigner':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        );
      case 'procurement':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      // Team submenu
      case 'user-management':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'tasks':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'team-capacity':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'team-performance':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        );
      // Requests & Approvals submenu
      case 'expenses':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'overtime':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'holidays':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      // Admin submenu
      case 'payments':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        );
      case 'invoices':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'quoting':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'clients':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'work-diary':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        );
      case 'progress-reports':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'photo-gallery':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      // Settings submenu
      case 'team-dashboard-settings':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
        );
      case 'plans':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        );
      case 'user-preferences':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case 'neo-credits':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      default:
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        );
    }
  };

  const menuItems = [
    { 
      key: 'dashboard', 
      label: 'Dashboard', 
      path: '/admin/dashboard',
      submenu: false,
      requiresFeature: 'dashboard'
    },
    { 
      key: 'sites', 
      label: 'Sites', 
      submenu: true,
      items: [
        { key: 'site-management', label: 'Sites', path: '/admin/sites', requiresFeature: 'siteManagement' },
        { key: 'site-assigner', label: 'Site Assigner', path: '/admin/site-assigner', requiresFeature: 'siteManagement' }
      ]
    },
    { 
      key: 'users', 
      label: 'Team', 
      submenu: true,
      items: [
        { key: 'user-management', label: 'Team', path: '/admin/users', requiresFeature: 'teamManagement' },
        { key: 'tasks', label: 'Tasks', path: '/admin/tasks', requiresFeature: 'taskManagement' },
        { key: 'team-capacity', label: 'Team Capacity', path: '/admin/team-capacity', requiresFeature: 'teamCapacity' },
        { key: 'team-performance', label: 'Team Performance', path: '/admin/reports/attendance', requiresFeature: 'teamPerformance' }
      ]
    },
    { 
      key: 'requests', 
      label: 'Requests & Approvals', 
      submenu: true,
      items: [
        { key: 'procurement', label: 'Materials', path: '/admin/procurement', requiresFeature: 'procurement' },
        { key: 'expenses', label: 'Expenses', path: '/admin/expenses', requiresFeature: 'expenses' },
        { key: 'holidays', label: 'Holidays', path: '/admin/holidays', requiresFeature: 'holidaysModule' },
        { key: 'overtime', label: 'Overtime', path: '/admin/overtime', requiresFeature: 'overtime' }
      ]
    },
    { 
      key: 'admin', 
      label: 'Admin', 
      submenu: true,
      items: [
        { key: 'clients', label: 'Clients', path: '/admin/clients' },
        { key: 'quoting', label: 'Quotes & Invoices', path: '/admin/quoting', requiresFeature: 'quotesAndInvoices' },
        { key: 'payments', label: 'Team Payments', path: '/admin/payments', requiresFeature: 'payments' },
        { key: 'invoices', label: 'CIS Statements', path: '/admin/invoices', requiresFeature: 'payments' },
        { key: 'work-diary', label: 'Work Diary', path: '/admin/reports/work-diary', requiresFeature: 'workDiary' },
        { key: 'photo-gallery', label: 'Photo Gallery', path: '/admin/photo-gallery', requiresFeature: 'photoGallery' },
        { key: 'progress-reports', label: 'Progress Reports', path: '/admin/reports/progress', requiresFeature: 'progressReports' }
      ]
    },
    { key: 'team-dashboard-settings', label: 'Admin Settings', path: '/admin/team-dashboard-settings' },
    { key: 'user-preferences', label: 'User Settings', path: '/admin/user-preferences' },
    { key: 'neo-credits', label: 'Neo Credits', path: '/admin/neo-credits', requiresFeature: 'neo' },
    { key: 'plans', label: 'Subscription Plans', path: '/admin/plans' }
  ];

  const isActiveItem = (item) => {
    if (item.submenu) {
      return item.items.some(subItem => location.pathname === subItem.path);
    }
    return location.pathname === item.path;
  };

  const isActiveSubItem = (subItem) => {
    return location.pathname === subItem.path;
  };

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Show loading spinner while checking auth */}
      {isChecking ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            <p className="mt-2 text-gray-400">Loading admin panel...</p>
          </div>
        </div>
      ) : !deviceCheckComplete ? (
        // Show loading while device check completes
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            <p className="mt-2 text-gray-400">Preparing your experience...</p>
          </div>
        </div>
      ) : isSmallScreenDevice ? (
        // Mobile Layout with Hamburger Menu
        <div className="min-h-screen bg-slate-900 flex flex-col overflow-x-hidden">
          {/* Mobile Header with Hamburger */}
          <header className="bg-gradient-to-r from-gray-900 to-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between sticky top-0 z-50">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
                aria-label="Open menu"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div>
                <h1 className="text-white font-semibold text-sm">SiteOrbit</h1>
                <p className="text-gray-400 text-xs">Admin Portal</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
              title="Sign Out"
            >
              <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
              </svg>
            </button>
          </header>

          {/* Mobile Slide-out Menu */}
          {mobileMenuOpen && (
            <div className="fixed inset-0 z-[100]">
              {/* Backdrop */}
              <div 
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={() => setMobileMenuOpen(false)}
              />
              
              {/* Menu Panel */}
              <div className="absolute left-0 top-0 bottom-0 w-72 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 shadow-2xl overflow-y-auto">
                {/* Menu Header */}
                <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                  <div>
                    <h2 className="text-white font-semibold">Menu</h2>
                    <p className="text-gray-400 text-xs mt-0.5">Navigate admin portal</p>
                  </div>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
                  >
                    <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Menu Items */}
                <nav className="p-3 space-y-1">
                  {/* Dashboard - Accessible */}
                  <button
                    onClick={() => {
                      navigate('/admin/dashboard');
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center space-x-3 transition-colors ${
                      location.pathname === '/admin/dashboard'
                        ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white'
                        : 'text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {getMenuIcon('dashboard')}
                    <span className="font-medium">Dashboard</span>
                  </button>

                  {/* Sites - Restricted */}
                  <button
                    onClick={() => {
                      navigate('/admin/sites');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 rounded-lg flex items-center space-x-3 text-gray-500 hover:bg-gray-700/50 transition-colors"
                  >
                    {getMenuIcon('sites')}
                    <span className="font-medium">Sites</span>
                    <svg className="w-4 h-4 text-amber-500 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </button>

                  {/* Team - Restricted */}
                  <button
                    onClick={() => {
                      navigate('/admin/users');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 rounded-lg flex items-center space-x-3 text-gray-500 hover:bg-gray-700/50 transition-colors"
                  >
                    {getMenuIcon('users')}
                    <span className="font-medium">Team</span>
                    <svg className="w-4 h-4 text-amber-500 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </button>

                  {/* Requests - Restricted */}
                  <button
                    onClick={() => {
                      navigate('/admin/expenses');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 rounded-lg flex items-center space-x-3 text-gray-500 hover:bg-gray-700/50 transition-colors"
                  >
                    {getMenuIcon('requests')}
                    <span className="font-medium">Requests & Approvals</span>
                    <svg className="w-4 h-4 text-amber-500 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </button>

                  {/* Admin - Restricted */}
                  <button
                    onClick={() => {
                      navigate('/admin/payments');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 rounded-lg flex items-center space-x-3 text-gray-500 hover:bg-gray-700/50 transition-colors"
                  >
                    {getMenuIcon('admin')}
                    <span className="font-medium">Admin</span>
                    <svg className="w-4 h-4 text-amber-500 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </button>

                  {/* Admin Settings - Restricted */}
                  <button
                    onClick={() => {
                      navigate('/admin/team-dashboard-settings');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 rounded-lg flex items-center space-x-3 text-gray-500 hover:bg-gray-700/50 transition-colors"
                  >
                    {getMenuIcon('team-dashboard-settings')}
                    <span className="font-medium">Admin Settings</span>
                    <svg className="w-4 h-4 text-amber-500 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </button>

                  {/* User Settings - Restricted */}
                  <button
                    onClick={() => {
                      navigate('/admin/user-preferences');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 rounded-lg flex items-center space-x-3 text-gray-500 hover:bg-gray-700/50 transition-colors"
                  >
                    {getMenuIcon('user-preferences')}
                    <span className="font-medium">User Settings</span>
                    <svg className="w-4 h-4 text-amber-500 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </button>

                  {/* Neo Credits - Accessible */}
                  <button
                    onClick={() => {
                      navigate('/admin/neo-credits');
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center space-x-3 transition-colors ${
                      location.pathname === '/admin/neo-credits'
                        ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white'
                        : 'text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {getMenuIcon('neo-credits')}
                    <span className="font-medium">Neo Credits</span>
                    <span className="ml-auto text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Mobile</span>
                  </button>

                  {/* Subscription Plans - Accessible */}
                  <button
                    onClick={() => {
                      navigate('/admin/plans');
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg flex items-center space-x-3 transition-colors ${
                      location.pathname === '/admin/plans'
                        ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white'
                        : 'text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {getMenuIcon('plans')}
                    <span className="font-medium">Subscription Plans</span>
                    <span className="ml-auto text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Mobile</span>
                  </button>
                </nav>

                {/* Menu Footer */}
                <div className="p-4 border-t border-gray-700 mt-auto">
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <p className="text-amber-200 text-xs">
                        Items with <span className="text-amber-400">desktop icon</span> require a screen width of 1024px or larger.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mobile Content Area */}
          <main className="flex-1 overflow-y-auto">
            {/* Subscription Restriction Overlay for Mobile */}
            {showRestrictionOverlay && <SubscriptionRestrictedOverlay />}
            
            {isCurrentRouteAccessible ? (
              // Show actual content for accessible routes
              <div className="p-4">
                {/* Grace Period Banner - Show on dashboard */}
                {location.pathname === '/admin/dashboard' && showGraceBanner && (
                  <GracePeriodBanner />
                )}
                
                {isLoading ? (
                  <div className="flex items-center justify-center p-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                    <span className="ml-3 text-gray-400">Loading...</span>
                  </div>
                ) : (
                  <Outlet context={{ users, sites, roles, departments, fetchData }} />
                )}
              </div>
            ) : (
              // Show gate message for restricted routes
              <div className="flex items-center justify-center min-h-[calc(100vh-64px)] p-6">
                <div className="max-w-md w-full text-center">
                  {/* Desktop Icon */}
                  <div className="mb-6">
                    <div className="w-20 h-20 mx-auto bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  
                  {/* Title */}
                  <h1 className="text-2xl font-bold text-white mb-3">
                    Larger Screen Required
                  </h1>
                  
                  {/* Message */}
                  <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-6 text-left">
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                      This feature requires a screen width of at least <strong className="text-white">1024 pixels</strong> (approximately a tablet in landscape mode or a laptop/desktop).
                    </p>
                    
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <p className="text-green-300 text-sm font-medium mb-1">Available on Mobile</p>
                          <p className="text-green-200 text-xs">
                            You can still access <strong>Dashboard</strong>, <strong>Neo Credits</strong>, and <strong>Subscription Plans</strong> from the menu.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-purple-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <p className="text-purple-300 text-sm font-medium mb-1">Use the Mobile App</p>
                          <p className="text-purple-200 text-xs">
                            For on-site work, download the <strong>SiteOrbit Mobile App</strong> for clock in/out, photo uploads, and task management.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <button
                      onClick={() => setMobileMenuOpen(true)}
                      className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-lg px-4 py-3 font-medium transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                      Open Menu
                    </button>
                    
                    {/* App Store Links */}
                    <div className="flex justify-center gap-3">
                      <a 
                        href="https://apps.apple.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 transition-colors"
                      >
                        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                        </svg>
                        <span className="text-white text-xs font-medium">App Store</span>
                      </a>
                      <a 
                        href="https://play.google.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 transition-colors"
                      >
                        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                        </svg>
                        <span className="text-white text-xs font-medium">Google Play</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      ) : (
        <>
          {/* Sidebar with independent scrolling - Dark Theme */}
          <div className="w-64 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 border-r border-gray-700 flex flex-col h-screen shadow-2xl relative z-50">
            {/* Header Section with Dashboard Title and Logout */}
            <div className="p-4 bg-gray-800 rounded-lg m-3 mb-2 border border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold text-sm">SiteOrbit</h3>
                  <p className="text-gray-400 text-xs mt-0.5">Admin Dashboard</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-white/30 transition-all duration-200 group"
                  title="Sign Out"
                >
                  <svg className="w-5 h-5 text-white group-hover:text-red-300 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                  </svg>
                </button>
              </div>
            </div>

            <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2 relative z-50">
              <div className="space-y-2">
              {menuItems.map((item) => {
                const isDisabled = item.requiresFeature && subscriptionFeatures && !subscriptionFeatures[item.requiresFeature];
                // Map menu keys to data-tour attributes
                const tourAttribute = {
                  'dashboard': 'dashboard',
                  'team-dashboard-settings': 'admin-settings',
                  'sites': 'sites',
                }[item.key];
                
                return (
                <div key={item.key} data-tour={tourAttribute}>
                  {isDisabled ? (
                    <div className="relative group" onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltipPos({
                        left: rect.right + 16,
                        top: rect.top
                      });
                    }}>
                      <button
                        disabled
                        className="w-full text-left px-4 py-3 text-sm font-semibold rounded-lg transition-all duration-200 bg-gray-700 text-gray-500 cursor-not-allowed opacity-60"
                      >
                        <div className="flex items-center space-x-3">
                          {getMenuIcon(item.key)}
                          <span>{item.label}</span>
                          <svg className="w-4 h-4 text-amber-500 ml-auto flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </button>
                      {/* Enhanced Tooltip */}
                      <div className="pointer-events-none fixed opacity-0 group-hover:opacity-100 transition-all duration-200 z-[10000]" style={{left: `${tooltipPos.left}px`, top: `${tooltipPos.top}px`}}>
                        <div className="relative bg-gradient-to-br from-amber-600 to-orange-600 text-white text-xs rounded-lg p-4 shadow-2xl border-2 border-amber-400 min-w-[300px]">
                          {/* Arrow pointing to the left */}
                          <div className="absolute right-full top-4 border-8 border-transparent border-r-amber-600"></div>
                          
                          {/* Icon and Title */}
                          <div className="flex items-start mb-2">
                            <svg className="w-5 h-5 text-amber-200 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                            <div>
                              <p className="font-bold text-sm text-white mb-1">Feature Locked</p>
                              <p className="text-amber-50 leading-relaxed">
                                This feature is not available on your current subscription tier.
                              </p>
                            </div>
                          </div>
                          
                          {/* Action Text */}
                          <div className="mt-3 pt-3 border-t border-amber-400/30">
                            <p className="text-amber-100 text-xs">
                              💡 Please visit the <span className="font-semibold">Plans Page</span> to upgrade your current subscription package.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                  <button
                    onClick={() => {
                      if (!item.submenu) {
                        safeNavigate(item.path);
                      }
                    }}
                    className={`w-full text-left px-4 py-3 text-sm font-semibold rounded-lg transition-all duration-200 ${
                      isActiveItem(item)
                        ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-lg'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    } ${item.submenu ? 'cursor-default' : ''}`}
                  >
                    <div className="flex items-center space-x-3">
                      {getMenuIcon(item.key)}
                      <span>{item.label}</span>
                    </div>
                  </button>
                  )}
                  
                  {item.submenu && (
                    <div className="ml-[22px] mt-1 space-y-1">
                      {item.items.map((subItem) => {
                        const isSubItemDisabled = subItem.requiresFeature && subscriptionFeatures && !subscriptionFeatures[subItem.requiresFeature];
                        
                        if (isSubItemDisabled) {
                          return (
                            <div key={subItem.key} className="relative group" onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setTooltipPos({
                                left: rect.right + 16,
                                top: rect.top
                              });
                            }}>
                              <button
                                disabled
                                className="w-full text-left px-4 py-2.5 text-sm rounded-lg transition-all duration-200 text-gray-600 cursor-not-allowed opacity-50"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2">
                                    {getSubMenuIcon(subItem.key, false, false)}
                                    <span>{subItem.label}</span>
                                  </div>
                                  <svg className="w-3 h-3 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              </button>
                              {/* Submenu Tooltip */}
                              <div className="pointer-events-none fixed opacity-0 group-hover:opacity-100 transition-all duration-200 z-[10000]" style={{left: `${tooltipPos.left}px`, top: `${tooltipPos.top}px`}}>
                                <div className="relative bg-gradient-to-br from-amber-600 to-orange-600 text-white text-xs rounded-lg p-4 shadow-2xl border-2 border-amber-400 min-w-[300px]">
                                  <div className="absolute right-full top-4 border-8 border-transparent border-r-amber-600"></div>
                                  <div className="flex items-start mb-2">
                                    <svg className="w-5 h-5 text-amber-200 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                      <p className="font-bold text-sm text-white mb-1">Feature Locked</p>
                                      <p className="text-amber-50 leading-relaxed">
                                        This feature is not available on your current subscription tier.
                                      </p>
                                    </div>
                                  </div>
                                  <div className="mt-3 pt-3 border-t border-amber-400/30">
                                    <p className="text-amber-100 text-xs">
                                      💡 Please visit the <span className="font-semibold">Plans Page</span> to upgrade your current subscription package.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        return (
                        <button
                          key={subItem.key}
                          data-tour={subItem.key === 'tasks' ? 'tasks' : subItem.key === 'user-management' ? 'team-members' : undefined}
                          onClick={() => safeNavigate(subItem.path)}
                          className={`w-full text-left px-4 py-2.5 text-sm rounded-lg transition-all duration-200 ${
                            isActiveSubItem(subItem)
                              ? 'bg-indigo-500 bg-opacity-20 text-indigo-300 border-l-2 border-indigo-500'
                              : 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              {getSubMenuIcon(subItem.key, isActiveSubItem(subItem), false)}
                              <span>{subItem.label}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              {subItem.key === 'expenses' && pendingExpensesCount > 0 && (
                                <span className="bg-indigo-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                                  {pendingExpensesCount}
                                </span>
                              )}
                              {subItem.key === 'overtime' && pendingOvertimeCount > 0 && (
                                <span className="bg-indigo-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                                  {pendingOvertimeCount}
                                </span>
                              )}
                              {subItem.key === 'holidays' && pendingHolidaysCount > 0 && (
                                <span className="bg-indigo-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                                  {pendingHolidaysCount}
                                </span>
                              )}
                              {subItem.key === 'procurement' && pendingProcurementCount > 0 && (
                                <span className="bg-indigo-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                                  {pendingProcurementCount}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      )})}
                    </div>
                  )}
                </div>
              )})}
              </div>
            </nav>

            {/* User Profile Card at Bottom */}
            {currentUser && (
              <div className="p-4 border-t border-gray-700 bg-gray-800">
                <div className="flex items-center space-x-3">
                  {/* Company Logo or Fallback */}
                  <div className="flex-shrink-0">
                    {organisationLogo ? (
                      <div className="w-12 h-12 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center p-1 border border-gray-600">
                        <img
                          src={organisationLogo}
                          alt={organisationName || 'Company Logo'}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center p-1 border border-gray-600">
                        <img
                          src="/SiteOrbit_Logo.png"
                          alt="SiteOrbit"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate">
                      {currentUser.full_name || `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'Admin User'}
                    </p>
                    {organisationName && (
                      <span 
                        className="inline-flex bg-purple-600 text-white text-xs font-medium px-2 py-0.5 rounded mt-1 truncate max-w-full"
                        data-testid="sidebar-org-badge"
                        title={organisationName}
                      >
                        {organisationName}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main Content with independent scrolling */}
          <div className="flex-1 min-w-0 flex flex-col h-screen">
            {/* Subscription Restriction Overlay */}
            {showRestrictionOverlay && <SubscriptionRestrictedOverlay />}
            
            {/* Scrollable Content Area */}
            <div className="flex-1 min-w-0 overflow-y-auto overflow-x-visible p-8">
              {/* Grace Period Banner - Show on dashboard */}
              {location.pathname === '/admin/dashboard' && showGraceBanner && (
                <GracePeriodBanner />
              )}
              
              {isLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                  <span className="ml-3 text-gray-400">Loading data...</span>
                </div>
              ) : (
                <Outlet context={{ users, sites, roles, departments, fetchData, fetchSubscriptionFeatures }} />
              )}
            </div>
          </div>
        </>
      )}
      
      {/* Guided Tour Overlay */}
      {showGuidedTour && (
        <GuidedTour onComplete={() => setShowGuidedTour(false)} />
      )}
    </div>
  );
};

// Main AdminLayout wrapper with SubscriptionProvider
const AdminLayout = () => {
  return (
    <SubscriptionProvider>
      <AdminLayoutInner />
    </SubscriptionProvider>
  );
};

export default AdminLayout;